/**
 * Cadeia de processamento do microfone.
 *
 * Duas camadas, que resolvem problemas diferentes e se complementam:
 *
 *   1. O processador do próprio navegador (cancelamento de eco, controle
 *      automático de ganho, supressão espectral). É obrigatório para o eco,
 *      porque só o navegador tem acesso ao sinal que está saindo pelos
 *      alto-falantes. Nenhum filtro nosso substitui isso.
 *
 *   2. RNNoise rodando em WebAssembly dentro de um AudioWorklet. É uma rede
 *      neural treinada para separar voz de ruído de fundo, e resolve o que o
 *      supressor espectral do navegador não pega: teclado mecânico, ventilador,
 *      obra na rua, cachorro latindo.
 *
 * O worklet roda na thread de áudio em tempo real, não na thread principal, e é
 * por isso que a interface não engasga enquanto ele processa.
 *
 * Depois do RNNoise vem uma porta de ruído com histerese: o RNNoise atenua o
 * ruído mas deixa um resíduo audível no silêncio, e a porta corta esse resíduo
 * por completo. Os dois limiares diferentes (abre em -45 dB, fecha em -55 dB)
 * evitam o efeito de a porta piscar quando o nível fica oscilando em torno de
 * um único valor.
 */

import {
  loadRnnoise,
  NoiseGateWorkletNode,
  RnnoiseWorkletNode,
} from '@sapphi-red/web-noise-suppressor'
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'

/**
 * Os dois processadores são servidos como arquivos estáticos, copiados no build
 * por `scripts/preparar-worklets.mjs`.
 *
 * Importá-los com `?url` direto do pacote não funciona: os dois se chamam
 * `workletProcessor.js` na origem, o bundler emite um só e o outro se perde.
 */
const rnnoiseWorkletUrl = '/worklets/rnnoise.js'
const noiseGateWorkletUrl = '/worklets/noise-gate.js'

/** RNNoise foi treinado a 48 kHz e só opera nessa taxa. */
const SAMPLE_RATE = 48_000

export interface AudioProcessingOptions {
  /** Cancelamento de eco do navegador. Desligar só faz sentido com fone. */
  echoCancellation: boolean
  /** Controle automático de ganho do navegador. */
  autoGainControl: boolean
  /** Supressor espectral do navegador. */
  browserNoiseSuppression: boolean
  /** RNNoise em WebAssembly. É o supressor "forte". */
  rnnoise: boolean
  /** Porta de ruído após o RNNoise, para silêncio absoluto entre as falas. */
  noiseGate: boolean
  /** Limiar de abertura da porta, em dB. */
  noiseGateThreshold: number
}

export const DEFAULT_AUDIO_OPTIONS: AudioProcessingOptions = {
  echoCancellation: true,
  autoGainControl: true,
  browserNoiseSuppression: true,
  rnnoise: true,
  noiseGate: true,
  noiseGateThreshold: -45,
}

/** O binário do RNNoise é baixado uma vez e reaproveitado entre conexões. */
let wasmBinaryPromise: Promise<ArrayBuffer> | null = null

function getRnnoiseBinary(): Promise<ArrayBuffer> {
  if (!wasmBinaryPromise) {
    wasmBinaryPromise = loadRnnoise({
      url: rnnoiseWasmUrl,
      simdUrl: rnnoiseSimdWasmUrl,
    }).catch((error: unknown) => {
      // Zera o cache para uma próxima tentativa poder acontecer.
      wasmBinaryPromise = null
      throw error
    })
  }
  return wasmBinaryPromise
}

/**
 * Prende a entrada do worklet a um canal só.
 *
 * Os worklets de supressão são criados com `maxChannels: 1` e processam um canal
 * apenas. Sem isto eles recebem os dois canais que o Chrome entrega na fonte do
 * microfone e descartam o segundo, o que gasta trabalho à toa e deixa a saída
 * com um canal vazio no meio da cadeia.
 *
 * Isto sozinho não resolve o canal mudo na faixa final: quem garante isso é a
 * duplicação explícita feita logo antes do destino.
 */
function forcarMono(node: AudioNode): void {
  node.channelCount = 1
  node.channelCountMode = 'explicit'
  node.channelInterpretation = 'speakers'
}

export interface ProcessedAudio {
  /** Faixa já processada, pronta para publicar no SFU. */
  track: MediaStreamTrack
  /** Nível de entrada de 0 a 1, para o indicador de fala. */
  getLevel: () => number
  /** Se o RNNoise realmente entrou na cadeia. */
  rnnoiseActive: boolean
  destroy: () => Promise<void>
}

export class MicrophoneProcessor {
  private context: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private rnnoiseNode: RnnoiseWorkletNode | null = null
  private gateNode: NoiseGateWorkletNode | null = null
  private analyser: AnalyserNode | null = null
  private merger: ChannelMergerNode | null = null
  private destination: MediaStreamAudioDestinationNode | null = null
  private rawStream: MediaStream | null = null
  private levelBuffer: Float32Array<ArrayBuffer> | null = null
  private workletsLoaded = false

  /**
   * Abre o microfone e monta a cadeia de processamento.
   *
   * Se o RNNoise falhar ao carregar, seguimos com o processamento nativo do
   * navegador em vez de deixar o usuário sem microfone. Voz com ruído é bem
   * melhor do que voz nenhuma.
   */
  async open(
    deviceId: string | undefined,
    options: AudioProcessingOptions,
  ): Promise<ProcessedAudio> {
    await this.close()

    const constraints: MediaTrackConstraints = {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      echoCancellation: options.echoCancellation,
      autoGainControl: options.autoGainControl,
      noiseSuppression: options.browserNoiseSuppression,
      channelCount: 1,
      sampleRate: SAMPLE_RATE,
    }

    this.rawStream = await navigator.mediaDevices.getUserMedia({ audio: constraints, video: false })

    const needsWorklet = options.rnnoise || options.noiseGate
    if (!needsWorklet) {
      // Sem processamento extra, entregamos a faixa crua e economizamos o custo
      // de manter um AudioContext vivo.
      const track = this.rawStream.getAudioTracks()[0]!
      return {
        track,
        getLevel: () => 0,
        rnnoiseActive: false,
        destroy: () => this.close(),
      }
    }

    const context = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: 'interactive' })
    this.context = context

    let rnnoiseActive = false

    try {
      /*
        Cada processador é carregado por conta própria.

        Antes os dois vinham num `Promise.all` dentro do mesmo try: a falha de um
        derrubava a montagem inteira e o usuário ficava com o microfone cru, sem
        supressão nenhuma e sem nada na tela indicando isso. Agora quem falha
        apenas sai da cadeia, e o resto continua funcionando.
      */
      const carregou = { rnnoise: false, gate: false }
      if (this.workletsLoaded) {
        carregou.rnnoise = true
        carregou.gate = true
      } else {
        const resultados = await Promise.allSettled([
          context.audioWorklet.addModule(rnnoiseWorkletUrl),
          context.audioWorklet.addModule(noiseGateWorkletUrl),
        ])
        carregou.rnnoise = resultados[0].status === 'fulfilled'
        carregou.gate = resultados[1].status === 'fulfilled'

        for (const [indice, nome] of [
          [0, 'RNNoise'],
          [1, 'porta de ruído'],
        ] as const) {
          const resultado = resultados[indice]
          if (resultado.status === 'rejected') {
            console.warn(`worklet da ${nome} não carregou`, resultado.reason)
          }
        }
        this.workletsLoaded = carregou.rnnoise && carregou.gate
      }

      this.source = context.createMediaStreamSource(this.rawStream)

      this.destination = context.createMediaStreamDestination()

      this.analyser = context.createAnalyser()
      this.analyser.fftSize = 512
      this.analyser.smoothingTimeConstant = 0.6
      this.levelBuffer = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4))

      let node: AudioNode = this.source

      if (options.rnnoise && carregou.rnnoise) {
        try {
          const wasmBinary = await getRnnoiseBinary()
          this.rnnoiseNode = new RnnoiseWorkletNode(context, { maxChannels: 1, wasmBinary })
          forcarMono(this.rnnoiseNode)
          node.connect(this.rnnoiseNode)
          node = this.rnnoiseNode
          rnnoiseActive = true
        } catch (error) {
          console.warn('RNNoise indisponível, seguindo com o supressor do navegador', error)
        }
      }

      if (options.noiseGate && carregou.gate) {
        try {
          this.gateNode = new NoiseGateWorkletNode(context, {
            openThreshold: options.noiseGateThreshold,
            // 10 dB de histerese impedem a porta de piscar em falas baixas.
            closeThreshold: options.noiseGateThreshold - 10,
            holdMs: 90,
            maxChannels: 1,
          })
          forcarMono(this.gateNode)
          node.connect(this.gateNode)
          node = this.gateNode
        } catch (error) {
          console.warn('porta de ruído indisponível, seguindo sem ela', error)
          this.gateNode = null
        }
      }

      node.connect(this.analyser)

      /*
        Os dois canais do destino recebem o mesmo sinal, explicitamente.

        Os worklets de supressão copiam para a saída apenas
        `min(canais de entrada, maxChannels)` canais, e `maxChannels` é 1. Como o
        Chrome entrega a fonte do microfone com dois canais mesmo quando o
        aparelho é mono, a saída do worklet nascia com dois canais e o direito
        nunca era escrito: silêncio absoluto de um lado, que chega do outro ponto
        da chamada tocando só num ouvido.

        Duplicar pelo somador resolve sem depender do navegador fazer o
        espelhamento sozinho, que é justamente o que não acontece aqui. Dois
        canais idênticos custam quase nada na compressão, porque o Opus codifica
        a diferença entre eles, e ela é zero.
      */
      const espelho = context.createChannelMerger(2)
      node.connect(espelho, 0, 0)
      node.connect(espelho, 0, 1)
      espelho.connect(this.destination)
      this.merger = espelho

      const track = this.destination.stream.getAudioTracks()[0]
      if (!track) throw new Error('cadeia de áudio não produziu faixa')

      return {
        track,
        getLevel: () => this.readLevel(),
        rnnoiseActive,
        destroy: () => this.close(),
      }
    } catch (error) {
      // Qualquer falha no worklet: desmonta e devolve o áudio direto do
      // navegador, que já tem cancelamento de eco e supressão básica.
      console.warn('falha ao montar a cadeia de áudio, usando faixa direta', error)
      await context.close().catch(() => {})
      this.context = null

      const track = this.rawStream.getAudioTracks()[0]!
      return {
        track,
        getLevel: () => 0,
        rnnoiseActive: false,
        destroy: () => this.close(),
      }
    }
  }

  /** Nível RMS convertido para uma escala perceptual de 0 a 1. */
  private readLevel(): number {
    if (!this.analyser || !this.levelBuffer) return 0

    this.analyser.getFloatTimeDomainData(this.levelBuffer)

    let sum = 0
    for (let i = 0; i < this.levelBuffer.length; i++) {
      const sample = this.levelBuffer[i] as number
      sum += sample * sample
    }
    const rms = Math.sqrt(sum / this.levelBuffer.length)

    // A raiz aproxima a curva de percepção de volume: sem ela, fala normal mal
    // move a barra e só grito chega perto do fim.
    return Math.min(1, Math.sqrt(rms) * 2.2)
  }

  async close(): Promise<void> {
    this.rnnoiseNode?.destroy()
    this.rnnoiseNode?.disconnect()
    this.gateNode?.disconnect()
    this.merger?.disconnect()
    this.analyser?.disconnect()
    this.source?.disconnect()
    this.destination?.disconnect()

    this.rnnoiseNode = null
    this.gateNode = null
    this.merger = null
    this.analyser = null
    this.source = null
    this.destination = null
    this.levelBuffer = null

    if (this.context) {
      await this.context.close().catch(() => {})
      this.context = null
      this.workletsLoaded = false
    }

    this.rawStream?.getTracks().forEach((track) => track.stop())
    this.rawStream = null
  }
}

/**
 * Enumera dispositivos.
 *
 * Os rótulos só aparecem depois de uma permissão concedida; antes disso o
 * navegador devolve strings vazias, e a interface precisa saber disso para
 * pedir permissão em vez de mostrar uma lista de "Dispositivo 1, 2, 3".
 */
export async function listAudioDevices(): Promise<{
  inputs: MediaDeviceInfo[]
  outputs: MediaDeviceInfo[]
  hasLabels: boolean
}> {
  const devices = await navigator.mediaDevices.enumerateDevices()
  const inputs = devices.filter((d) => d.kind === 'audioinput')
  const outputs = devices.filter((d) => d.kind === 'audiooutput')
  return {
    inputs,
    outputs,
    hasLabels: inputs.some((d) => d.label.length > 0),
  }
}

export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((t) => t.stop())
    return true
  } catch {
    return false
  }
}
